import type { ReactNode } from 'react';
import {
  Button,
  Checkbox,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@@/ui/primitives';
import { Plus, Search } from 'lucide-react';

export interface ColumnDef<T> {
  key: string;
  header: string;
  render: (item: T) => ReactNode;
  headerClassName?: string;
  cellClassName?: string;
}

interface DataTableProps<T extends { id: string }> {
  title: string;
  data: T[];
  totalCount: number;
  columns: ColumnDef<T>[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  searchLabel?: string;
  selectedIds?: Set<string>;
  onSelectionChange?: (id: string) => void;
  onSelectAll?: () => void;
  showCheckboxes?: boolean;
  createButtonLabel?: string;
  onCreateClick?: () => void;
  showLoadMore?: boolean;
  onLoadMore?: () => void;
}

export const DataTable = <T extends { id: string }>({
  title,
  data,
  totalCount,
  columns,
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Search…',
  searchLabel,
  selectedIds,
  onSelectionChange,
  onSelectAll,
  showCheckboxes = true,
  createButtonLabel,
  onCreateClick,
  showLoadMore,
  onLoadMore
}: DataTableProps<T>) => {
  const allSelected = data.length > 0 && selectedIds?.size === data.length;

  return (
    <div className='bg-card rounded-lg border border-border flex-1 flex flex-col min-w-0'>
      <div className='p-4 border-b border-border'>
        <h4 className='text-xl font-semibold text-foreground mb-3'>
          {title} ({totalCount.toLocaleString()})
        </h4>
        {searchLabel && <p className='text-sm font-medium text-foreground mb-2'>{searchLabel}</p>}
        <div className='flex items-center gap-3'>
          <div className='relative'>
            <Input
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className='pr-8 text-sm! w-72'
            />
            <Search className='absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
          </div>
          {createButtonLabel && onCreateClick && (
            <Button variant='outline' size='sm' onClick={onCreateClick}>
              <Plus className='h-4 w-4' />
              {createButtonLabel}
            </Button>
          )}
        </div>
      </div>
      <div className='overflow-x-auto flex-1'>
        <Table className='w-full'>
          <TableHeader>
            <TableRow className='bg-muted/50'>
              {showCheckboxes && (
                <TableHead className='w-10 pl-4'>
                  <Checkbox checked={allSelected} onCheckedChange={onSelectAll} />
                </TableHead>
              )}
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={column.headerClassName || 'text-xs font-semibold text-foreground'}
                >
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item) => (
              <TableRow key={item.id} className='hover:bg-muted/50'>
                {showCheckboxes && (
                  <TableCell className='pl-4'>
                    <Checkbox
                      checked={selectedIds?.has(item.id)}
                      onCheckedChange={() => onSelectionChange?.(item.id)}
                    />
                  </TableCell>
                )}
                {columns.map((column) => (
                  <TableCell key={column.key} className={column.cellClassName}>
                    {column.render(item)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {showLoadMore && onLoadMore && (
        <div className='p-4 border-t border-border'>
          <Button variant='outline' size='sm' onClick={onLoadMore}>
            <Plus className='h-4 w-4' />
            Load More
          </Button>
        </div>
      )}
    </div>
  );
};
